import { Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, Slider, TextField, Typography } from '@mui/material';
import { Theme, ThemeProvider } from '@mui/material/styles';
import { ChangeEvent, JSX, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { absoluteBrandingUrl, useBranding } from '../../../contexts/BrandingContext';
import { parseApiError } from '../../../library/apiErrorHelper';
import { currentTenantId } from '../../../library/brandingCache';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import {
    NumericBounds,
    WATERMARK_OPACITY_BOUNDS,
    WATERMARK_SCALE_BOUNDS,
} from '../../../library/validationRules';
import { splitApiError } from '../../../library/useApiFormError';
// Only `isValidBrandColor` is called here, to resolve the preview's own fallback. The other
// three predicates moved into `brandingSchema` and were left imported; they are not a second
// opinion this file gets to hold, so they go.
import { BrandingDraft, IBranding, isValidBrandColor, toBrandingDraft, toBrandingRequest } from '../../../library/models/tenantBranding';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { CAREFULL_PRIMARY, CAREFULL_SECONDARY, brandCssVariables, contrastRatio, contrastTextFor, deriveBrandTokens } from '../../../theme/brandTokens';
import { createAppTheme } from '../../../theme';
import { useSnackbar } from '../../layout/snackbarProvider';
import { createBrandingSchema } from './brandingSchema';
import { LogoCropDialog } from '../../imageCrop/LogoCropDialog';
import {
    watermarkActionsStyles,
    watermarkControlsStyles,
    watermarkFallbackStyles,
    watermarkMarkStyles,
    watermarkPageHeadingStyles,
    watermarkPageLinesStyles,
    watermarkPageStyles,
    watermarkPreviewLabelStyles,
    watermarkRowStyles,
    watermarkSliderLabelStyles,
    watermarkSliderRowStyles,
    watermarkSliderValueStyles,
} from './brandingSettings.styles';
import { actionsStyles, cardHintStyles, cardStyles, languageFieldStyles, cardTitleStyles, colourFieldStyles, colourRowStyles, colourSwatchStyles, faviconPreviewStyles, hexFieldStyles, linkFieldStyles, linkRowStyles, logoActionsStyles, logoImageStyles, logoPreviewStyles, logoRowStyles, previewAccentBarStyles, previewCardBodyStyles, previewCardStyles, previewEyebrowStyles, previewMastheadLabelStyles, previewMastheadLogoStyles, previewMastheadNameStyles, previewMastheadStyles, previewRowStyles, previewSurfaceStyles, sectionStyles, warningStyles } from './brandingSettings.styles';

/** WCAG AA for body text. Below this, a filled button's label is hard to read. */
const CONTRAST_FLOOR = 4.5;

/**
 * Where a clinic chooses how the app looks to its own people.
 *
 * Two colours and a logo, and nothing else -- everything the UI needs beyond them is
 * derived, so there is no way to set the hover shade and the tint separately and no way for
 * them to disagree. Leaving a colour blank means "use Carefull's", which is also the reset.
 *
 * The preview is a real MUI subtree under its own ThemeProvider, plus the `--ch-*` variables
 * scoped to the same box. Both are needed: MUI components read the theme, and the accent bar
 * and eyebrow read the variables. Together they show what the page will actually look like
 * rather than two swatches that leave the admin guessing.
 *
 * The contrast check **warns and does not block**. It is the clinic's brand; the app's job
 * is to tell them their button text will be hard to read, not to overrule them.
 *
 * The preview opens on the **email masthead**, which is deliberate and is the reason it is
 * first rather than last: an email is the one surface a clinic's branding reaches that nobody
 * on this page can otherwise see, and it went wrong unnoticed once already. The masthead used
 * to paint the clinic's colour behind the logo, which erased any mark drawn for the light
 * surfaces everything else here uses; it is now a rule above and below with the logo on white,
 * and this preview has to keep saying whichever of those the template actually does.
 */
export const BrandingSettings = (): JSX.Element => {
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { logoSrc, logoAlt } = useBranding();
    const fileInput = useRef<HTMLInputElement>(null);
    const [pendingLogo, setPendingLogo] = useState<File | null>(null);
    const [isOpeningLogo, setIsOpeningLogo] = useState<boolean>(false);

    const faviconInput = useRef<HTMLInputElement>(null);
    const [pendingFavicon, setPendingFavicon] = useState<File | null>(null);
    const [isOpeningFavicon, setIsOpeningFavicon] = useState<boolean>(false);

    const watermarkInput = useRef<HTMLInputElement>(null);
    const [pendingWatermark, setPendingWatermark] = useState<File | null>(null);
    const [isOpeningWatermark, setIsOpeningWatermark] = useState<boolean>(false);

    /**
     * The stored watermark, held as an object URL rather than pointed at with a plain `src`.
     *
     * Its route is the one piece of branding that is **not** public: nothing outside this
     * card ever looks at a watermark, so it takes the clinic from the session and needs the
     * bearer token, which an `<img>` cannot send. Same shape as `MedicalRecordsTimeline` fetching
     * a PDF blob, and the reason this is state rather than a derived value.
     */
    const [watermarkSrc, setWatermarkSrc] = useState<string | null>(null);

    // The same key BrandingProvider reads, so saving repaints the whole app and not just
    // this page's preview.
    const { data, isLoading, refetch } = useReactQuery<IBranding>(
        services.tenantBranding.getBranding(currentTenantId())
    );

    const saveBranding = useReactMutation(services.tenantBranding.saveBrandingMutation());
    const uploadLogo = useReactMutation(services.tenantBranding.uploadLogoMutation());
    const removeLogo = useReactMutation(services.tenantBranding.removeLogoMutation());
    const uploadFavicon = useReactMutation(services.tenantBranding.uploadFaviconMutation());
    const removeFavicon = useReactMutation(services.tenantBranding.removeFaviconMutation());
    const uploadWatermark = useReactMutation(services.tenantBranding.uploadWatermarkMutation());
    const removeWatermark = useReactMutation(services.tenantBranding.removeWatermarkMutation());

    /** Null until the first load, so the fields open on what is actually published. */
    const schema = useMemo(() => createBrandingSchema(translate), [translate]);

    const {
        control, handleSubmit, reset, setValue, setError, formState: { errors, isValid },
    } = useValidatedForm<BrandingDraft>(schema, { defaultValues: toBrandingDraft(undefined) });

    /** Reseeded from the server, so the fields open on what the clinic actually has. */
    useEffect(() => {
        if (data) {
            reset(toBrandingDraft(data));
        }
    }, [data, reset]);

    const current: BrandingDraft = useWatch({ control }) as BrandingDraft;

    const primary: string = isValidBrandColor(current.primaryColor) && current.primaryColor
        ? current.primaryColor
        : CAREFULL_PRIMARY;
    const secondary: string = isValidBrandColor(current.secondaryColor) && current.secondaryColor
        ? current.secondaryColor
        : CAREFULL_SECONDARY;

    const previewTheme: Theme = useMemo(
        () => createAppTheme(primary, secondary),
        [primary, secondary]
    );

    // Scoped to the preview box so the draft is shown without touching the live app: the
    // real variables stay on <html> until the save lands.
    const previewVariables = useMemo(
        () => brandCssVariables(deriveBrandTokens(primary, secondary)),
        [primary, secondary]
    );

    /**
     * What the page preview draws, and it is the same three-step fallback the server applies
     * when it renders a document: the clinic's watermark, else its logo, else nothing.
     *
     * `logoSrc` is deliberately not used for the middle rung -- that one falls back again to
     * the Carefull mark, and a clinic with no artwork of its own prints a *blank* page rather
     * than the platform's logo. Showing Carefull's here would promise a mark no document will
     * ever carry, the same trap `hasOwnLogo` exists to avoid on the email masthead.
     */
    const markSrc: string | null =
        watermarkSrc ?? (data?.logoUrl ? absoluteBrandingUrl(data.logoUrl, data.logoEtag) : null);

    // Clamped, not merely parsed. The sliders cannot produce anything outside the bounds, but
    // a half-typed or server-echoed value can be NaN, and `opacity: NaN` silently renders the
    // mark at full strength -- the one wrong answer that looks like a deliberate choice.
    const markOpacity: number = clampSetting(current.watermarkOpacity, WATERMARK_OPACITY_BOUNDS);
    const markScale: number = clampSetting(current.watermarkScale, WATERMARK_SCALE_BOUNDS);

    const unreadable: string[] = [primary, secondary].filter(
        (colour: string) => contrastRatio(colour, contrastTextFor(colour)) < CONTRAST_FLOOR
    );

    /**
     * What the browser tab will actually draw: the clinic's favicon when it has one, and its
     * logo when it has not -- the same order `BrandingContext` feeds `useDocumentIdentity`.
     *
     * `logoSrc` for the second rung and not `data.logoUrl`, unlike `markSrc` above, because
     * here the last rung is right: a clinic with no artwork at all is genuinely showing
     * Carefull's mark in its tab, since `index.html` ships that favicon and nothing has
     * replaced it. Drawing an empty square would be the lie in this direction.
     */
    const faviconPreviewSrc: string =
        absoluteBrandingUrl(data?.faviconUrl, data?.faviconEtag) ?? logoSrc;

    // The clinic's own logo, not `logoSrc` -- that falls back to Carefull's mark, and an email
    // never does. `EmailBrandingResolver` sends no logo at all when a clinic has none, so that
    // a patient is not shown a brand they have never heard of above their own appointment; the
    // masthead below has to fall back the same way, or it would promise a logo that no message
    // will carry.
    const hasOwnLogo: boolean = Boolean(data?.logoUrl);

    // What the email signs itself as: `clinic_name`, which is the clinic's display name and
    // never Carefull's. Taken from the response rather than the context so the card does not
    // depend on a provider above it having resolved the same clinic.
    const clinicName: string = data?.displayName ?? logoAlt;

    // One rule, one evaluation: `brandingSchema` wraps the same predicates that mark each
    // field, so a value the button accepts is never one the API then refuses.
    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);

    const update = (field: keyof BrandingDraft, value: string): void =>
        setValue(field, value, { shouldValidate: true });

    const onValid = (values: BrandingDraft): void => {
        saveBranding.mutate(toBrandingRequest(values), {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.BRANDING_SAVE_SUCCESS), 'success');
                refetch();
            },
            onError: (error: Error) => {
                const outcome = splitApiError(error, knownFields, translate,
                    ResourcesIds.BRANDING_SAVE_ERROR);

                Object.entries(outcome.fieldErrors).forEach(([field, message]) =>
                    setError(field as keyof BrandingDraft, { type: 'server', message }));

                if (outcome.formError) {
                    showSnackbar(outcome.formError, 'error');
                }
            },
        });
    };

    /**
     * Colours only. An admin asking for the platform's palette back has not asked to take
     * their Instagram link down, and the two undos are a click apart -- clearing a field
     * is how a link goes.
     */
    const onResetColours = (): void => {
        setValue('primaryColor', '', { shouldValidate: true });
        setValue('secondaryColor', '', { shouldValidate: true });
    };

    /**
     * Picking a file no longer uploads it. The crop is the subject of the upload, and an
     * admin who picked the wrong image finds out in the dialog rather than in the header.
     */
    const onPickLogo = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        // Cleared straight away so choosing the same file twice still fires a change event.
        event.target.value = '';

        if (!file) {
            return;
        }

        setPendingLogo(file);
    };

    /**
     * Closes on success only. A rejected upload leaves the dialog open with the crop as the
     * admin left it, so retrying does not start with picking the file again.
     */
    const onCroppedLogo = (cropped: File): void => {
        uploadLogo.mutate(cropped, {
            onSuccess: () => {
                setPendingLogo(null);
                showSnackbar(translate(ResourcesIds.BRANDING_LOGO_SUCCESS), 'success');
                refetch();
            },
            onError: (error: Error) =>
                showSnackbar(
                    parseApiError(error).message ?? translate(ResourcesIds.BRANDING_LOGO_ERROR),
                    'error'
                ),
        });
    };

    /**
     * Reopens the crop dialog on the logo that is already live.
     *
     * What is stored is the *cropped* image, not the file the admin first picked -- that one
     * is gone. So this can tighten a crop but never widen it, which is fine: widening is
     * what "Upload a logo" is for, and the admin still has the original. Cropping further is
     * pixel-exact rather than lossy, because the output never upscales.
     */
    const onEditLogo = async (): Promise<void> => {
        if (!data?.logoUrl) {
            return;
        }

        setIsOpeningLogo(true);

        try {
            setPendingLogo(
                await services.tenantBranding.fetchLogoFile(currentTenantId() ?? 0, data.logoEtag)
            );
        } catch (error) {
            showSnackbar(translate(ResourcesIds.BRANDING_LOGO_EDIT_ERROR), 'error');
        } finally {
            setIsOpeningLogo(false);
        }
    };

    /** The favicon's three handlers, which are the logo's with the noun changed. */
    const onPickFavicon = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) {
            return;
        }

        setPendingFavicon(file);
    };

    const onCroppedFavicon = (cropped: File): void => {
        uploadFavicon.mutate(cropped, {
            onSuccess: () => {
                setPendingFavicon(null);
                showSnackbar(translate(ResourcesIds.BRANDING_FAVICON_SUCCESS), 'success');
                refetch();
            },
            onError: (error: Error) =>
                showSnackbar(
                    parseApiError(error).message ?? translate(ResourcesIds.BRANDING_FAVICON_ERROR),
                    'error'
                ),
        });
    };

    const onEditFavicon = async (): Promise<void> => {
        if (!data?.faviconUrl) {
            return;
        }

        setIsOpeningFavicon(true);

        try {
            setPendingFavicon(
                await services.tenantBranding.fetchFaviconFile(
                    currentTenantId() ?? 0,
                    data.faviconEtag
                )
            );
        } catch (error) {
            showSnackbar(translate(ResourcesIds.BRANDING_FAVICON_EDIT_ERROR), 'error');
        } finally {
            setIsOpeningFavicon(false);
        }
    };

    /**
     * Keeps the preview's object URL in step with what is stored, and revokes the one it
     * replaces.
     *
     * Keyed on the ETag rather than on the URL: the path never changes, so an admin who
     * re-crops would otherwise keep looking at the image before last. A clinic with no
     * watermark of its own clears the URL and the card falls back to showing the *logo*,
     * which is what its documents will actually carry.
     */
    useEffect(() => {
        if (!data?.watermarkUrl) {
            setWatermarkSrc(null);
            return;
        }

        let revoked = false;
        let url: string | null = null;

        services.tenantBranding
            .fetchWatermarkFile(data.watermarkEtag)
            .then((file: File) => {
                url = URL.createObjectURL(file);

                // The fetch outlived the card, or a newer one has already landed. Revoking
                // straight away rather than setting state avoids both a leak and a warning.
                if (revoked) {
                    URL.revokeObjectURL(url);
                    return;
                }

                setWatermarkSrc(url);
            })
            .catch(() => setWatermarkSrc(null));

        return () => {
            revoked = true;

            if (url) {
                URL.revokeObjectURL(url);
            }
        };
    }, [data?.watermarkUrl, data?.watermarkEtag]);

    const onPickWatermark = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        // Cleared straight away so choosing the same file twice still fires a change event.
        event.target.value = '';

        if (file) {
            setPendingWatermark(file);
        }
    };

    const onCroppedWatermark = (cropped: File): void => {
        uploadWatermark.mutate(cropped, {
            onSuccess: () => {
                setPendingWatermark(null);
                showSnackbar(translate(ResourcesIds.BRANDING_WATERMARK_SUCCESS), 'success');
                refetch();
            },
            onError: (error: Error) =>
                showSnackbar(
                    parseApiError(error).message ?? translate(ResourcesIds.BRANDING_WATERMARK_ERROR),
                    'error'
                ),
        });
    };

    /** Reopens the crop on the stored watermark, with the caveat `onEditLogo` documents. */
    const onEditWatermark = async (): Promise<void> => {
        if (!data?.watermarkUrl) {
            return;
        }

        setIsOpeningWatermark(true);

        try {
            setPendingWatermark(
                await services.tenantBranding.fetchWatermarkFile(data.watermarkEtag)
            );
        } catch (error) {
            showSnackbar(translate(ResourcesIds.BRANDING_WATERMARK_EDIT_ERROR), 'error');
        } finally {
            setIsOpeningWatermark(false);
        }
    };

    const onRemoveWatermark = (): void => {
        removeWatermark.mutate(undefined, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.BRANDING_WATERMARK_REMOVED), 'success');
                refetch();
            },
            onError: (error: Error) =>
                showSnackbar(
                    parseApiError(error).message ?? translate(ResourcesIds.BRANDING_WATERMARK_ERROR),
                    'error'
                ),
        });
    };

    const onRemoveFavicon = (): void => {
        removeFavicon.mutate(undefined, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.BRANDING_FAVICON_REMOVED), 'success');
                refetch();
            },
            onError: (error: Error) =>
                showSnackbar(
                    parseApiError(error).message ?? translate(ResourcesIds.BRANDING_FAVICON_ERROR),
                    'error'
                ),
        });
    };

    const onRemoveLogo = (): void => {
        removeLogo.mutate(undefined, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.BRANDING_LOGO_REMOVED), 'success');
                refetch();
            },
            onError: (error: Error) =>
                showSnackbar(
                    parseApiError(error).message ?? translate(ResourcesIds.BRANDING_LOGO_ERROR),
                    'error'
                ),
        });
    };

    if (isLoading) {
        return (
            <Box sx={sectionStyles}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={sectionStyles}>
            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.BRANDING_COLOURS_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.BRANDING_COLOURS_HINT)}
                </Typography>

                <Box sx={colourRowStyles}>
                    <ColourField
                        label={translate(ResourcesIds.BRANDING_PRIMARY_LABEL)}
                        value={current.primaryColor}
                        resolved={primary}
                        onChange={(value: string) => update('primaryColor', value)}
                        {...fieldProps(errors, 'primaryColor', ' ')}
                    />
                    <ColourField
                        label={translate(ResourcesIds.BRANDING_SECONDARY_LABEL)}
                        value={current.secondaryColor}
                        resolved={secondary}
                        onChange={(value: string) => update('secondaryColor', value)}
                        {...fieldProps(errors, 'secondaryColor', ' ')}
                    />
                </Box>

                {unreadable.length > 0 && (
                    <Alert severity="warning" sx={warningStyles}>
                        {translate(ResourcesIds.BRANDING_CONTRAST_WARNING)}
                    </Alert>
                )}
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.BRANDING_PREVIEW_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.BRANDING_PREVIEW_HINT)}
                </Typography>

                <ThemeProvider theme={previewTheme}>
                    <Box sx={previewSurfaceStyles} style={previewVariables as React.CSSProperties}>
                        <Typography variant="caption" sx={previewMastheadLabelStyles}>
                            {translate(ResourcesIds.BRANDING_PREVIEW_MASTHEAD)}
                        </Typography>
                        <Box sx={previewMastheadStyles}
                            style={{ borderTopColor: primary, borderBottomColor: primary }}>
                            {hasOwnLogo ? (
                                <Box component="img" src={logoSrc} alt={clinicName}
                                    sx={previewMastheadLogoStyles} />
                            ) : (
                                <Box sx={previewMastheadNameStyles}>
                                    {clinicName}
                                </Box>
                            )}
                        </Box>

                        <Box sx={previewRowStyles}>
                            <Button variant="contained" color="primary">
                                {translate(ResourcesIds.BRANDING_PREVIEW_PRIMARY_ACTION)}
                            </Button>
                            <Button variant="contained" color="secondary">
                                {translate(ResourcesIds.BRANDING_PREVIEW_SECONDARY_ACTION)}
                            </Button>
                            <Button variant="outlined" color="primary">
                                {translate(ResourcesIds.BRANDING_PREVIEW_OUTLINED_ACTION)}
                            </Button>
                            <Chip color="primary" label={translate(ResourcesIds.BRANDING_PREVIEW_CHIP)} />
                        </Box>

                        <Box sx={previewCardStyles}>
                            <Box sx={previewAccentBarStyles} />
                            <Box sx={previewCardBodyStyles}>
                                <Box sx={previewEyebrowStyles}>
                                    {translate(ResourcesIds.BRANDING_PREVIEW_EYEBROW)}
                                </Box>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {translate(ResourcesIds.BRANDING_PREVIEW_CARD_TITLE)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translate(ResourcesIds.BRANDING_PREVIEW_CARD_BODY)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </ThemeProvider>
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.BRANDING_LOGO_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.BRANDING_LOGO_HINT)}
                </Typography>

                <Box sx={logoRowStyles}>
                    <Box sx={logoPreviewStyles}>
                        <Box component="img" src={logoSrc} alt="" sx={logoImageStyles} />
                    </Box>

                    <Box sx={logoActionsStyles}>
                        <Button
                            variant="outlined"
                            onClick={() => fileInput.current?.click()}
                            disabled={uploadLogo.isPending}
                        >
                            {translate(ResourcesIds.BRANDING_LOGO_UPLOAD)}
                        </Button>

                        {data?.logoUrl && (
                            <Button
                                variant="outlined"
                                onClick={onEditLogo}
                                disabled={isOpeningLogo || uploadLogo.isPending}
                            >
                                {translate(ResourcesIds.BRANDING_LOGO_EDIT)}
                            </Button>
                        )}

                        {/* Outlined like the two beside it, so all three read as one set and
                            the theme's hover lift has an outline to lift -- a borderless button
                            picks up the same shadow with no edge to hold it and reads as a stray
                            white card. The colour, not a lighter weight, is what marks this one
                            as the destructive choice. */}
                        {data?.logoUrl && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={onRemoveLogo}
                                disabled={removeLogo.isPending}
                            >
                                {translate(ResourcesIds.BRANDING_LOGO_REMOVE)}
                            </Button>
                        )}

                        {!data?.logoUrl && (
                            <Typography variant="caption" color="text.secondary">
                                {translate(ResourcesIds.BRANDING_LOGO_NONE)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <input
                    ref={fileInput}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    data-testid="logo-file-input"
                    hidden
                    onChange={onPickLogo}
                />

                <LogoCropDialog
                    file={pendingLogo}
                    isUploading={uploadLogo.isPending}
                    onCancel={() => setPendingLogo(null)}
                    onConfirm={onCroppedLogo}
                />
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.BRANDING_FAVICON_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.BRANDING_FAVICON_HINT)}
                </Typography>

                <Box sx={logoRowStyles}>
                    {/* What the tab will actually show, which is the favicon when there is
                        one and the logo when there is not -- so an admin who has uploaded
                        nothing here sees the thing they are being offered a replacement for,
                        rather than an empty square. */}
                    <Box sx={faviconPreviewStyles}>
                        <Box component="img" src={faviconPreviewSrc} alt="" sx={logoImageStyles} />
                    </Box>

                    <Box sx={logoActionsStyles}>
                        <Button
                            variant="outlined"
                            onClick={() => faviconInput.current?.click()}
                            disabled={uploadFavicon.isPending}
                        >
                            {translate(ResourcesIds.BRANDING_FAVICON_UPLOAD)}
                        </Button>

                        {data?.faviconUrl && (
                            <Button
                                variant="outlined"
                                onClick={onEditFavicon}
                                disabled={isOpeningFavicon || uploadFavicon.isPending}
                            >
                                {translate(ResourcesIds.BRANDING_FAVICON_EDIT)}
                            </Button>
                        )}

                        {data?.faviconUrl && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={onRemoveFavicon}
                                disabled={removeFavicon.isPending}
                            >
                                {translate(ResourcesIds.BRANDING_FAVICON_REMOVE)}
                            </Button>
                        )}

                        {!data?.faviconUrl && (
                            <Typography variant="caption" color="text.secondary">
                                {translate(ResourcesIds.BRANDING_FAVICON_NONE)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <input
                    ref={faviconInput}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    data-testid="favicon-file-input"
                    hidden
                    onChange={onPickFavicon}
                />

                <LogoCropDialog
                    file={pendingFavicon}
                    variant="favicon"
                    isUploading={uploadFavicon.isPending}
                    onCancel={() => setPendingFavicon(null)}
                    onConfirm={onCroppedFavicon}
                />
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.BRANDING_WATERMARK_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.BRANDING_WATERMARK_HINT)}
                </Typography>

                <Box sx={watermarkRowStyles}>
                    <Box>
                        <Typography variant="caption" sx={watermarkPreviewLabelStyles}>
                            {translate(ResourcesIds.BRANDING_WATERMARK_PREVIEW_LABEL)}
                        </Typography>

                        <Box sx={watermarkPageStyles} data-testid="watermark-page-preview">
                            <Box sx={watermarkPageHeadingStyles}>
                                {translate(ResourcesIds.BRANDING_WATERMARK_PREVIEW_HEADING)}
                            </Box>
                            <Box sx={watermarkPageLinesStyles} />

                            {markSrc && (
                                <Box
                                    component="img"
                                    src={markSrc}
                                    alt=""
                                    data-testid="watermark-mark"
                                    sx={watermarkMarkStyles}
                                    style={{ width: `${markScale}%`, opacity: markOpacity / 100 }}
                                />
                            )}
                        </Box>
                    </Box>

                    <Box sx={watermarkControlsStyles}>
                        <Box sx={watermarkActionsStyles}>
                            <Button
                                variant="outlined"
                                onClick={() => watermarkInput.current?.click()}
                                disabled={uploadWatermark.isPending}
                            >
                                {translate(ResourcesIds.BRANDING_WATERMARK_UPLOAD)}
                            </Button>

                            {data?.watermarkUrl && (
                                <Button
                                    variant="outlined"
                                    onClick={onEditWatermark}
                                    disabled={isOpeningWatermark || uploadWatermark.isPending}
                                >
                                    {translate(ResourcesIds.BRANDING_WATERMARK_EDIT)}
                                </Button>
                            )}

                            {data?.watermarkUrl && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={onRemoveWatermark}
                                    disabled={removeWatermark.isPending}
                                >
                                    {translate(ResourcesIds.BRANDING_WATERMARK_REMOVE)}
                                </Button>
                            )}
                        </Box>

                        {/* The sliders stay live whether the mark is the clinic's watermark
                            or its logo -- a clinic on the fallback is printing at exactly
                            these numbers, so hiding them would hide a setting that is in
                            force. */}
                        <WatermarkSlider
                            label={translate(ResourcesIds.BRANDING_WATERMARK_OPACITY_LABEL)}
                            bounds={WATERMARK_OPACITY_BOUNDS}
                            value={markOpacity}
                            onChange={(value: number) =>
                                update('watermarkOpacity', String(value))}
                        />

                        <WatermarkSlider
                            label={translate(ResourcesIds.BRANDING_WATERMARK_SCALE_LABEL)}
                            bounds={WATERMARK_SCALE_BOUNDS}
                            value={markScale}
                            onChange={(value: number) => update('watermarkScale', String(value))}
                        />

                        {!data?.watermarkUrl && (
                            <Typography variant="caption" sx={watermarkFallbackStyles}>
                                {translate(hasOwnLogo
                                    ? ResourcesIds.BRANDING_WATERMARK_FALLBACK_LOGO
                                    : ResourcesIds.BRANDING_WATERMARK_FALLBACK_NONE)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <input
                    ref={watermarkInput}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    data-testid="watermark-file-input"
                    hidden
                    onChange={onPickWatermark}
                />

                <LogoCropDialog
                    file={pendingWatermark}
                    isUploading={uploadWatermark.isPending}
                    variant="watermark"
                    onCancel={() => setPendingWatermark(null)}
                    onConfirm={onCroppedWatermark}
                />
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.BRANDING_SOCIAL_TITLE)}
                </Typography>
                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.BRANDING_SOCIAL_HINT)}
                </Typography>

                <Box sx={linkRowStyles}>
                    <LinkField
                        label={translate(ResourcesIds.BRANDING_SOCIAL_INSTAGRAM_LABEL)}
                        value={current.instagramUrl}
                        placeholder="https://www.instagram.com/your-clinic/"
                        onChange={(value: string) => update('instagramUrl', value)}
                        {...fieldProps(errors, 'instagramUrl', ' ')}
                    />
                    <LinkField
                        label={translate(ResourcesIds.BRANDING_SOCIAL_FACEBOOK_LABEL)}
                        value={current.facebookUrl}
                        placeholder="https://www.facebook.com/your-clinic"
                        onChange={(value: string) => update('facebookUrl', value)}
                        {...fieldProps(errors, 'facebookUrl', ' ')}
                    />
                    <LinkField
                        label={translate(ResourcesIds.BRANDING_SOCIAL_WHATSAPP_LABEL)}
                        value={current.whatsappNumber}
                        placeholder="+40 7xx xxx xxx"
                        onChange={(value: string) => update('whatsappNumber', value)}
                        {...fieldProps(errors, 'whatsappNumber', ' ')}
                    />
                    <LinkField
                        label={translate(ResourcesIds.BRANDING_SOCIAL_MAPS_LABEL)}
                        value={current.mapsUrl}
                        placeholder="https://maps.app.goo.gl/..."
                        onChange={(value: string) => update('mapsUrl', value)}
                        {...fieldProps(errors, 'mapsUrl', ' ')}
                    />
                    <LinkField
                        label={translate(ResourcesIds.BRANDING_CONTACT_EMAIL_LABEL)}
                        value={current.contactEmail}
                        placeholder="contact@your-clinic.ro"
                        onChange={(value: string) => update('contactEmail', value)}
                        {...fieldProps(errors, 'contactEmail', ' ')}
                    />
                </Box>

                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.BRANDING_CONTACT_EMAIL_HINT)}
                </Typography>
            </Paper>

            <Paper sx={cardStyles} elevation={0}>
                <Typography variant="h6" sx={cardTitleStyles}>
                    {translate(ResourcesIds.BRANDING_DEFAULT_LANGUAGE_TITLE)}
                </Typography>

                <FormControl size="small" sx={languageFieldStyles}>
                    {/* `shrink` and `notched` together, because the control is deliberately
                        readable while empty: an unset default shows "No default language"
                        rather than a blank box an admin has to open to understand. */}
                    <InputLabel shrink id="branding-default-language">
                        {translate(ResourcesIds.BRANDING_DEFAULT_LANGUAGE_LABEL)}
                    </InputLabel>
                    <Select
                        labelId="branding-default-language"
                        label={translate(ResourcesIds.BRANDING_DEFAULT_LANGUAGE_LABEL)}
                        displayEmpty
                        notched
                        value={current.defaultLanguage}
                        onChange={(event) => update('defaultLanguage', event.target.value)}
                    >
                        {/* Blank is a real option, not a placeholder: clearing it is how a
                            clinic stops having a default and hands every visitor back to
                            their own browser. */}
                        <MenuItem value="">
                            {translate(ResourcesIds.BRANDING_DEFAULT_LANGUAGE_NONE)}
                        </MenuItem>
                        <MenuItem value="ro">{translate(ResourcesIds.LANGUAGE_RO)}</MenuItem>
                        <MenuItem value="en">{translate(ResourcesIds.LANGUAGE_EN)}</MenuItem>
                    </Select>
                </FormControl>

                <Typography variant="body2" sx={cardHintStyles}>
                    {translate(ResourcesIds.BRANDING_DEFAULT_LANGUAGE_HINT)}
                </Typography>
            </Paper>

            <Box sx={actionsStyles}>
                <Button
                    variant="contained"
                    onClick={handleSubmit(onValid)}
                    disabled={!isValid || saveBranding.isPending}
                >
                    {translate(ResourcesIds.BRANDING_SAVE)}
                </Button>
                <Button variant="text" onClick={onResetColours} disabled={saveBranding.isPending}>
                    {translate(ResourcesIds.BRANDING_RESET)}
                </Button>
            </Box>
        </Box>
    );
};

/**
 * One colour: a native picker and the hex beside it, both editing the same value.
 *
 * The picker always shows *something* -- it cannot represent "unset" -- so it is fed the
 * resolved colour while the text field shows the real value, blank included. That is what
 * lets an admin clear the field to fall back to the platform's while still seeing what the
 * platform's is.
 */
const ColourField = ({
    label,
    value,
    resolved,
    onChange,
    error,
    helperText,
}: {
    label: string;
    value: string;
    resolved: string;
    onChange: (value: string) => void;
    error: boolean;
    helperText?: string;
}): JSX.Element => {
    return (
        <Box sx={colourFieldStyles}>
            <Box
                component="input"
                type="color"
                aria-label={label}
                value={resolved}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onChange(event.target.value.toUpperCase())
                }
                sx={colourSwatchStyles}
            />
            <TextField
                label={label}
                value={value}
                onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                placeholder={resolved}
                error={error}
                // A space rather than nothing, so an appearing message never shifts the row.
                helperText={helperText ?? ' '}
                size="small"
                sx={hexFieldStyles}
            />
        </Box>
    );
};

/**
 * One way of being reached: a full-width field, told whether it is wrong rather than
 * working it out.
 *
 * It used to take the predicate as a prop -- the WhatsApp field is a phone number and the
 * other three are URLs. `brandingSchema` names which rule each field answers to now, so
 * this is layout and nothing else. Empty stays valid there: clearing the field is how a
 * clinic takes a link down.
 */
/**
 * A draft value as a percentage the preview can use, never NaN.
 *
 * The sliders cannot produce anything out of range, so this is about the values that do not
 * come from them: an empty draft on first render, and whatever a future server or a hand-
 * edited row sends back. `opacity: NaN` renders at full strength rather than not at all,
 * which is the one wrong answer that looks like somebody meant it.
 */
const clampSetting = (value: string, bounds: NumericBounds): number => {
    const parsed: number = Number(value);

    if (!Number.isFinite(parsed)) {
        return bounds.min;
    }

    return Math.min(bounds.max, Math.max(bounds.min, Math.round(parsed)));
};

/**
 * One of the two watermark settings: a label, a slider, and the number it is on.
 *
 * A slider rather than a numeric field because neither of these is a value anybody knows in
 * advance -- "how faint should this be" is answered by looking at the page beside it, not by
 * typing. The reading is printed anyway, because a slider alone cannot be checked against
 * what a colleague set last month.
 *
 * `bounds` is the same declaration `brandingSchema` builds its `integerIn` rule from, so the
 * control can never offer a value the form would then refuse.
 */
const WatermarkSlider = ({
    label,
    bounds,
    value,
    onChange,
}: {
    label: string;
    bounds: NumericBounds;
    value: number;
    onChange: (value: number) => void;
}): JSX.Element => (
    <Box sx={watermarkSliderRowStyles}>
        <Typography variant="body2" sx={watermarkSliderLabelStyles} id={`watermark-${label}`}>
            {label}
        </Typography>

        <Slider
            value={value}
            min={bounds.min}
            max={bounds.max}
            step={bounds.step ?? 1}
            aria-labelledby={`watermark-${label}`}
            onChange={(_event: Event, next: number | number[]) =>
                onChange(Array.isArray(next) ? next[0] : next)}
        />

        <Typography variant="body2" sx={watermarkSliderValueStyles}>
            {value}%
        </Typography>
    </Box>
);

const LinkField = ({
    label,
    value,
    placeholder,
    onChange,
    error,
    helperText,
}: {
    label: string;
    value: string;
    placeholder: string;
    onChange: (value: string) => void;
    error: boolean;
    helperText?: string;
}): JSX.Element => (
    <TextField
        label={label}
        value={value}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        placeholder={placeholder}
        error={error}
        // A space rather than nothing, so an appearing message never shifts the row.
        helperText={helperText ?? ' '}
        size="small"
        sx={linkFieldStyles}
    />
);
